const SW_ENCRYPTED_BLOB_VERSION = 0x01;

export interface EncryptedBlob {
  version: number;
  nonce: Uint8Array;
  ciphertext: Uint8Array;
}

export function encodeBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export function decodeBase64(value: string): Uint8Array {
  const binary = atob(value);
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

export function encodeBase64Url(bytes: Uint8Array): string {
  return encodeBase64(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function decodeBase64Url(value: string): Uint8Array {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const padLength = (4 - (padded.length % 4)) % 4;
  const binary = atob(padded + '='.repeat(padLength));
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

export async function sha256(data: Uint8Array): Promise<Uint8Array> {
  const input = new Uint8Array(data);
  const digest = await crypto.subtle.digest('SHA-256', input);
  return new Uint8Array(digest);
}

export async function encryptAesGcm(
  key: CryptoKey,
  plaintext: Uint8Array,
  aad?: Uint8Array,
): Promise<EncryptedBlob> {
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const iv = new Uint8Array(nonce);
  const algorithm: AesGcmParams = { name: 'AES-GCM', iv };
  if (aad) {
    algorithm.additionalData = new Uint8Array(aad);
  }

  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt(algorithm, key, new Uint8Array(plaintext)),
  );

  return { version: SW_ENCRYPTED_BLOB_VERSION, nonce, ciphertext };
}

export async function decryptAesGcm(
  key: CryptoKey,
  blob: EncryptedBlob,
  aad?: Uint8Array,
): Promise<Uint8Array> {
  if (blob.version !== SW_ENCRYPTED_BLOB_VERSION) {
    throw new Error('unsupported_blob_version');
  }

  const iv = new Uint8Array(blob.nonce);
  const algorithm: AesGcmParams = { name: 'AES-GCM', iv };
  if (aad) {
    algorithm.additionalData = new Uint8Array(aad);
  }

  const plaintext = await crypto.subtle.decrypt(
    algorithm,
    key,
    new Uint8Array(blob.ciphertext),
  );

  return new Uint8Array(plaintext);
}

export function packEncryptedBlob(blob: EncryptedBlob): Uint8Array {
  const output = new Uint8Array(1 + blob.nonce.length + blob.ciphertext.length);
  output[0] = blob.version;
  output.set(blob.nonce, 1);
  output.set(blob.ciphertext, 1 + blob.nonce.length);
  return output;
}

export function unpackEncryptedBlob(bytes: Uint8Array): EncryptedBlob {
  if (bytes.length < 14 || bytes[0] !== SW_ENCRYPTED_BLOB_VERSION) {
    throw new Error('invalid_encrypted_blob');
  }

  return {
    version: bytes[0],
    nonce: bytes.slice(1, 13),
    ciphertext: bytes.slice(13),
  };
}

export async function importAesKey(rawKey: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', new Uint8Array(rawKey), { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt',
  ]);
}

export function randomBytes(length: number): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(length));
}

export async function hkdfSha256(
  ikm: Uint8Array,
  salt: Uint8Array,
  info: string,
  length: number,
): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey('raw', new Uint8Array(ikm), 'HKDF', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(salt),
      info: new TextEncoder().encode(info),
    },
    key,
    length * 8,
  );
  return new Uint8Array(bits);
}
